import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocation, useNavigate, useRoutes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useAuthStore } from "@/store/auth.store";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import "./index.css";
import { protectedRoutes, publicRoutes } from "./routes/index";
import { generateRoutes } from "./routes/utils";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5, // 5 minutes
			refetchOnWindowFocus: false,
			refetchOnMount: true,
			retry: 1,
			gcTime: 1000 * 60 * 5 // 5 minutes
		}
	}
});

function App() {
	const navigate = useNavigate();
	const location = useLocation();
	const accessToken = useAuthStore((state) => state.accessToken);
	const resetAuth = useAuthStore((state) => state.resetAuth);

	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (
				e.key === "accessToken" &&
				!e.newValue &&
				!location.pathname.startsWith("/accept-invitation") &&
				!location.pathname.startsWith("/reset-password")
			) {
				resetAuth();
				navigate("/login", { replace: true });
			}
		};

		window.addEventListener("storage", handleStorageChange);

		return () => {
			window.removeEventListener("storage", handleStorageChange);
		};
	}, [navigate, resetAuth, location.pathname]);

	useEffect(() => {
		// Check if current path is accept-invitation or reset-password
		const isExemptPath =
			location.pathname.startsWith("/accept-invitation") ||
			location.pathname.startsWith("/reset-password") ||
			location.pathname === "/login";

		if (!accessToken && !isExemptPath) {
			resetAuth();
			navigate("/login", { replace: true });
		}
	}, [accessToken, location.pathname, navigate, resetAuth]);

	const isAuthenticated = !!accessToken;
	const routes = generateRoutes(publicRoutes, protectedRoutes, isAuthenticated);
	const element = useRoutes(routes);

	return (
		<>
			{element}
			<ToastContainer
				position="bottom-right"
				autoClose={3000}
				hideProgressBar={false}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
			/>
		</>
	);
}

function AppWrapper() {
	return (
		<QueryClientProvider client={queryClient}>
			<App />
		</QueryClientProvider>
	);
}

export default AppWrapper;
